import { stripHtml } from '@libs/common-next';

import type { AssistedSearchState, ProductSearchItem, SearchRedirect, SearchSuggestionItem } from '../search.types';
import { getSearchSuggestionAssistName } from '../utils';
import { readSearchProductById } from './search-data';

const NUMERIC_PRODUCT_QUERY = /^\d{7}(?:\d{2})?$/;
const SKU_QUERY = /^\d{7}$/;

export type SearchSubmitDestination = 'cancelled' | 'product' | 'redirect' | 'search-results';

function getRedirectUrl(redirects: readonly SearchRedirect[]) {
    return redirects[0]?.urls[0];
}

function getNumericProductUrl(product: ProductSearchItem, query: string) {
    return SKU_QUERY.test(query) ? product.skuProductUrl : product.masterProductUrl;
}

export function getSearchResultUrl(item: SearchSuggestionItem, query: string) {
    const category = item.category ? `&category=${encodeURIComponent(item.category)}` : '';
    const assistName = getSearchSuggestionAssistName(item);

    return `/search?q=${encodeURIComponent(stripHtml(item.label.trim()))}${category}&format=new&st=assisted&sa=${encodeURIComponent(assistName)}&sterm=${encodeURIComponent(query)}&sat=${encodeURIComponent(query)}`;
}

export function getProductResultUrl(item: ProductSearchItem, query: string) {
    return getNumericProductUrl(item, query);
}

export function getSearchSubmitUrl(query: string, assistedSearch: AssistedSearchState) {
    if (!assistedSearch.isModified) {
        return `/search?q=${encodeURIComponent(query.trim())}&st=manual`;
    }

    return `/search?q=${encodeURIComponent(query)}&st=assisted&sa=${encodeURIComponent(assistedSearch.searchAssistName)}&sat=${encodeURIComponent(assistedSearch.searchAssistTerm)}&sterm=${encodeURIComponent(assistedSearch.searchTerm)}`;
}

type StartApplicationSearchSubmitOptions = {
    applicationRedirects: readonly SearchRedirect[];
    assistedSearch: AssistedSearchState;
    isActive: () => boolean;
    loadedProductShortcut: ProductSearchItem | undefined;
    onFetchedProduct: () => void;
    onLoadedProduct: () => void;
    onRedirect: () => void;
    onSearchResultsFallback: (url: string) => void;
    onSubmit: () => void;
    query: string;
};

export async function startApplicationSearchSubmit({
    applicationRedirects,
    assistedSearch,
    isActive,
    loadedProductShortcut,
    onFetchedProduct,
    onLoadedProduct,
    onRedirect,
    onSearchResultsFallback,
    onSubmit,
    query,
}: StartApplicationSearchSubmitOptions): Promise<SearchSubmitDestination> {
    onSubmit();
    const applicationRedirectUrl = getRedirectUrl(applicationRedirects);

    if (applicationRedirectUrl !== undefined) {
        onRedirect();
        window.location.href = applicationRedirectUrl;
        return 'redirect';
    }

    if (NUMERIC_PRODUCT_QUERY.test(query)) {
        if (loadedProductShortcut) {
            onLoadedProduct();
            window.location.href = getNumericProductUrl(loadedProductShortcut, query);
            return 'product';
        }

        const product = await readSearchProductById(query);
        if (!isActive()) return 'cancelled';

        if (product) {
            onFetchedProduct();
            window.location.href = getNumericProductUrl(product, query);
            return 'product';
        }
    } else {
        await Promise.resolve();
        if (!isActive()) return 'cancelled';
    }

    const searchResultsUrl = getSearchSubmitUrl(query, assistedSearch);
    onSearchResultsFallback(searchResultsUrl);
    window.location.href = searchResultsUrl;
    return 'search-results';
}

export function completeCoreRedirectSubmit(coreRedirects: readonly SearchRedirect[]) {
    const coreRedirectUrl = getRedirectUrl(coreRedirects);
    if (coreRedirectUrl) window.location.assign(coreRedirectUrl);
}

export function navigateToSearchResult(url: string) {
    window.location.href = url;
}
